import React, { useState } from 'react';
import { DotsHorizontalIcon, EyeIcon } from '@heroicons/react/outline';

import { REQUIRED_RULE } from 'utils/constants';
import { classNames } from 'utils/functions';

export default function SignupStep3({
  buildBaseInput,
  setValue,
  register,
}: {
  buildBaseInput: any;
  setValue: any;
  register: any;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<number | undefined>();

  const testPasswordStrength = (value: string) => {
    if (!value) return setPasswordStrength(undefined);
    const strongRegex = new RegExp(
        '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[=/()%ยง!@#$%^&*])(?=.{8,})'
      ),
      mediumRegex = new RegExp(
        '^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})'
      );
    if (strongRegex.test(value)) setPasswordStrength(2);
    else if (mediumRegex.test(value)) setPasswordStrength(1);
    else setPasswordStrength(0);
  };

  return (
    <div className="mx-auto max-w-md">
      {buildBaseInput('Contraseña', showPassword ? 'text' : 'password', 'password', REQUIRED_RULE, {
        buttonContent: (
          <div className="flex items-center">
            {showPassword ? (
              <DotsHorizontalIcon className="h-4 w-4" />
            ) : (
              <EyeIcon className="h-4 w-4" />
            )}
          </div>
        ),
        buttonAction: () => setShowPassword(!showPassword),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          testPasswordStrength(e.target.value);
          setValue('password', e.target.value);
        },
      })}
      <div className="mt-2 flex space-x-1 px-1">
        <div
          className={classNames(
            passwordStrength !== undefined ? 'w-1/3' : 'w-0',
            passwordStrength === 0 ? 'bg-red-500' : '',
            passwordStrength === 1 ? 'bg-orange-500' : '',
            passwordStrength === 2 ? 'bg-green-500' : '',
            'h-2 rounded-sm  transition-all duration-500'
          )}
        />
        <div
          className={classNames(
            passwordStrength !== undefined && passwordStrength > 0 ? 'w-1/3' : 'w-0',
            passwordStrength === 1 ? 'bg-orange-500' : '',
            passwordStrength === 2 ? 'bg-green-500' : '',
            'h-2 rounded-sm  transition-all duration-500'
          )}
        />
        <div
          className={classNames(
            passwordStrength === 2 ? 'w-1/3' : 'w-0',
            'h-2 rounded-sm bg-green-500 transition-all duration-500'
          )}
        />
      </div>
      {passwordStrength !== undefined && (
        <p
          className={classNames(
            'text-sm transition-all duration-500',
            ['text-red-600', 'text-orange-600', 'text-green-600'][passwordStrength]
          )}
        >
          {
            ['Contraseña poco segura', 'Contraseña segura', 'Contraseña muy segura'][
              passwordStrength
            ]
          }
        </p>
      )}
      <p className="mt-1 text-sm">
        Se recomienda usar una combinación de letras, números y caracteres especiales.
      </p>
      <fieldset className="mt-4 space-y-2">
        <legend className="sr-only">Aceptar términos</legend>
        <div className="relative flex items-start">
          <div className="flex h-5 items-center">
            <input
              id="terms_checkbox"
              name="terms"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              {...register('terms', { REQUIRED_RULE })}
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms_checkbox" className="text-gray-600">
              Acepto los{' '}
              <a
                className="underline hover:text-gray-800"
                href="/document/empresa"
                target="_blank"
                rel="noreferrer"
              >
                términos y condiciones
              </a>{' '}
              (*)
            </label>
          </div>
        </div>
        <div className="relative flex items-start">
          <div className="flex h-5 items-center">
            <input
              id="policy_checkbox"
              name="policy"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              {...register('policy', { REQUIRED_RULE })}
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="policy_checkbox" className="text-gray-600">
              Acepto de forma expresa e informada la{' '}
              <a
                className="underline hover:text-gray-800"
                href="/document/privacidad"
                target="_blank"
                rel="noreferrer"
              >
                Política de Tratamiendo de Datos Personales
              </a>{' '}
              de Izi Dólar (*)
            </label>
          </div>
        </div>
        <div className="relative flex items-start">
          <div className="flex h-5 items-center">
            <input
              id="offers_checkbox"
              name="offers"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              {...register('offers')}
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="offers_checkbox" className="text-gray-600">
              Quiero recibir ofertas y promociones a mi correo electrónico
            </label>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
